import { ExportFile } from '@insurance/components'
import { getFileName, validateFileType } from '@shared/utils'
import { App, Button, Col, Form, Row, Space, Upload } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { EditPersonsFields } from '../PersonsFields'
import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import { useStyles } from './styles'
import {
  formProps,
  MIN_AGE_ACCIDENT_INSURANCE,
  personsRestrictions
} from './utils'

export const EditPersons = () => {
  const { isEditable } = useAccidentInsuranceValues()
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const {
    personsCountProps,
    personsProps,
    contractTypeProps,
    personsStr,
    prevContractTypeKey,
    prevPersonsCountKey
  } = formProps()
  const personsCount = Form.useWatch(personsCountProps.name, { preserve: true })
  const persons = Form.useWatch(personsProps.name, { preserve: true })
  const contractType = Form.useWatch(contractTypeProps.name, { preserve: true })
  const prevPersonsCount = Form.useWatch(prevPersonsCountKey.name, {
    preserve: true
  })
  const prevContractType = Form.useWatch(prevContractTypeKey.name, {
    preserve: true
  })
  const personsDocument = Form.useWatch(personsStr.name, {
    preserve: true
  })
  const formInstance = Form.useFormInstance()

  useEffect(() => {
    if (!personsCount || !contractType) return
    formInstance.setFieldValue(prevPersonsCountKey.name, personsCount)
    formInstance.setFieldValue(prevContractTypeKey.name, contractType)
    if (personsCount === prevPersonsCount && contractType === prevContractType)
      return

    formInstance.setFieldValue(
      personsProps.name,
      Array.from({ length: personsCount })
    )
  }, [
    contractType,
    formInstance,
    personsCount,
    personsProps.name,
    prevContractType,
    prevContractTypeKey.name,
    prevPersonsCount,
    prevPersonsCountKey.name
  ])

  const { addShow, deleteShow, personRange } = personsRestrictions(
    contractType,
    personsCount,
    persons?.length
  )

  const personsDocumentFile = personsDocument?.fileList?.[0]

  const onRemovePersonsDocument = () => {
    formInstance.setFields([
      { name: personsProps.name, errors: [], value: persons },
      { name: personsStr.name, errors: [] }
    ])
  }

  useEffect(() => {
    if (!isEditable || !personRange) return

    formInstance.setFieldValue(prevPersonsCountKey.name, personRange)
    formInstance.setFieldValue(personsCountProps.name, personRange)
  }, [
    formInstance,
    isEditable,
    personRange,
    personsCountProps.name,
    prevPersonsCountKey.name
  ])

  return (
    <>
      <Row gutter={12}>
        <Col xs={24} lg={18}>
          <Form.Item
            name={personsStr.name}
            rules={[
              {
                validator: (_, value) =>
                  validateFileType(
                    value,
                    t('error_invalid_file_general_type'),
                    [
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'text/csv'
                    ],
                    ['.csv', '.xlsx']
                  )
              }
            ]}
          >
            <Upload
              accept=".xlsx,.csv"
              maxCount={1}
              onChange={({ file }) =>
                file.status === 'done' &&
                message.success(t('file_has_been_uploaded_successfully'))
              }
              fileList={personsDocumentFile ? [personsDocumentFile] : []}
              customRequest={({ onSuccess }) => onSuccess?.({})}
              onRemove={onRemovePersonsDocument}
              style={{ width: '100%' }}
            >
              <Button block type="primary">
                {t('add_multiple_persons_from_csv_xlsx')}
              </Button>
            </Upload>
          </Form.Item>

          {!!personsDocumentFile && (
            <Form.Item
              name={personsProps.name}
              className={styles.formOnlyError}
            />
          )}
        </Col>

        <Col xs={24} lg={6}>
          <Space.Compact block>
            <ExportFile
              url="/entities/persons/import-xlsx-template/"
              name={getFileName('template_xlsx', 'xlsx')}
              block
              buttonName={t('template_xlsx')}
            />
            <ExportFile
              url="/entities/persons/import-csv-template/"
              name={getFileName('template_csv', 'csv')}
              block
              buttonName={t('template_csv')}
            />
          </Space.Compact>
        </Col>
      </Row>

      {!personsDocumentFile && (
        <EditPersonsFields
          resident
          short
          minAge={MIN_AGE_ACCIDENT_INSURANCE}
          personsKey={personsProps.name}
          showAddAction={!!isEditable || addShow}
          showDeleteAction={() =>
            isEditable ? persons?.length > 2 : deleteShow
          }
        />
      )}

      {!personsDocumentFile && (
        <Form.Item name={personsProps.name} className={styles.formOnlyError} />
      )}
    </>
  )
}
